import {
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import './preview.css';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  AppDispatch,
  RootState,
} from 'packages/configurazione-components/src/store';
import { useDispatch, useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { BluDialog, BluLoader } from '@bludata/components';
import { QRCode } from 'react-qrcode-logo';
import { updatePreviewShopSign } from '../../api/update-preview-shop-sign';
import { useWatch } from 'react-hook-form';
import { resetCustomTheme, setCustomTheme } from '../../store/custom-theme';
import { resetPalette, setPalette } from '../../store/palette';
import { resetFont, setFont } from '../../store/font';
import { resetComponents, setComponents } from '../../store/components';
import { getDefaultShopSign } from '../../lib/white-cache';
import { defaultCustomTheme } from '../../utils/default-custom-theme';

interface PreviewInterface {
  shopSignId?: string;
  disabled?: boolean;
}

function deepEqual(a, b) {
  if (a === b) return true;

  if (typeof a !== typeof b) return false;

  if (typeof a !== 'object' || a === null || b === null) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    // eslint-disable-next-line no-prototype-builtins
    if (!b.hasOwnProperty(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
}

export const Preview: FC<PreviewInterface> = ({ shopSignId, disabled }) => {
  const token = useWatch({ name: 'token' });
  const bluBookingLink = useWatch({ name: 'bluBookingLink' });

  const prevPreviewRef = useRef(null);
  const url = `${bluBookingLink}preview/$${token}`;
  const palette = useSelector((state: RootState) => state.layoutPalette);
  const typography = useSelector((state: RootState) => state.layoutFont);
  const components = useSelector((state: RootState) => state.layoutComponents);
  const customTheme = useSelector(
    (state: RootState) => state.layoutCustomTheme
  );
  const [timeStamp, setTimeStamp] = useState(Date.now());
  const [refresh, setRefresh] = useState(false);
  const [qrCodePopup, setQrCodePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    const iframe = document.getElementById('previewB2C');
    if (iframe === null) return;

    const data = {
      payload: {
        configurationJson: { palette, typography, components },
        customConfigurationJson: customTheme,
      },
      source: 'blu-booking-preview',
    };

    console.log('customTheme', customTheme);

    const sendMessage = () => {
      (iframe as any).contentWindow.postMessage(data, '*');
    };

    iframe.onload = sendMessage;

    // Invia subito se l’iframe è già caricato
    if ((iframe as any).contentWindow) {
      sendMessage();
    }

    return () => {
      iframe.onload = null;
    };
  }, [palette, typography, components, customTheme, refresh]);

  const handleSavePreview = useCallback(async () => {
    setIsLoading(true);
    await updatePreviewShopSign(
      shopSignId,
      { palette, typography, components },
      customTheme
    );
    setIsLoading(false);
  }, [components, customTheme, palette, shopSignId, typography]);

  const handleQrPreview = useCallback(async () => {
    setIsLoading(true);
    const result = await updatePreviewShopSign(
      shopSignId,
      { palette, typography, components },
      customTheme
    );
    setIsLoading(false);
    if (result) {
      setQrCodePopup(true);
    }
  }, [components, customTheme, palette, shopSignId, typography]);

  useEffect(() => {
    const message = {
      previewConfigurationJson: { palette, typography, components },
      previewCustomConfigurationJson: customTheme,
    };
    if (disabled) {
      prevPreviewRef.current = message;

      return;
    }

    const initialShopSign = getDefaultShopSign();
    if (!prevPreviewRef.current) {
      const config = JSON.parse(initialShopSign.configurationJson);
      prevPreviewRef.current = {
        previewConfigurationJson: {
          palette: config.palette,
          typography: config.typography,
          components: config.components,
        },
        previewCustomConfigurationJson: {},
      };
      if (initialShopSign?.customConfigurationJson) {
        prevPreviewRef.current.previewCustomConfigurationJson = JSON.parse(
          initialShopSign.customConfigurationJson
        );
      }
    }

    if (!deepEqual(prevPreviewRef.current, message)) {
      const delayFn = setTimeout(async () => {
        updatePreviewShopSign(
          shopSignId,
          message.previewConfigurationJson,
          message.previewCustomConfigurationJson
        );
        prevPreviewRef.current = message;
      }, 1000);

      return () => {
        clearTimeout(delayFn);
      };
    }
  }, [components, customTheme, disabled, palette, shopSignId, typography]);

  const getHeight = () =>
    !disabled ? window.innerHeight - 200 : window.innerHeight - 140;

  // const getHeight = () =>
  //   !disabled ? window.innerHeight - 150 : window.innerHeight - 85;

  return (
    <Stack flex="1.5" style={{ position: 'relative' }}>
      <BluLoader open={isLoading} />
      <BluDialog
        type="warning"
        confirmButton="Salva"
        controllConfirm={false}
        sx={{
          '& .MuiPaper-root': {
            maxWidth: 500,
          },
        }}
        dialogContent={
          <Stack flex="1" gap={2}>
            <Typography>
              Scansiona il QR code con il tuo smartphone per visualizzare
              un’anteprima del sito.
            </Typography>
            <Stack justifyItems={'center'} alignItems="center">
              <QRCode
                value={url}
                size={250}
                qrStyle="squares"
                eyeColor="#1375c0"
                eyeRadius={10}
              />
            </Stack>
          </Stack>
        }
        open={qrCodePopup}
        dialogTitle={'Anteprima del sito'}
        draggable
        dialogActions={
          <Stack direction="row" flex="1" justifyContent="end">
            <Button onClick={() => setQrCodePopup(false)} variant="outlined">
              Chiudi
            </Button>
          </Stack>
        }
        zIndex={0}
      />
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100, // assicurati che stia sopra il contenuto
          maxHeight: getHeight(),
        }}
      >
        <Stack direction={'row'} justifyContent="end">
          {!disabled && (
            <Tooltip title="Salva anteprima">
              <IconButton
                sx={{ height: '40px', width: '40px' }}
                size="small"
                onClick={handleSavePreview}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Prova anteprima">
            <IconButton
              sx={{ height: '40px', width: '40px' }}
              size="small"
              onClick={handleQrPreview}
            >
              <QrCodeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Aggiorna anteprima">
            <IconButton
              sx={{ height: '40px', width: '40px' }}
              size="small"
              onClick={() => {
                setTimeStamp(Date.now());
                setTimeout(() => {
                  setRefresh(!refresh);
                }, 600);
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack flex="1" gap={1}>
          <div style={{ width: '100%', height: 'auto' }}>
            <div style={{ width: '100%', height: '100%' }}>
              <iframe
                id="previewB2C"
                title="B2C"
                src={`${url}?cacheTime=${timeStamp}`}
                height={getHeight()}
                width="100%"
                style={{
                  zoom: 0.75,
                  border: 'none',
                  display: 'block',
                  width: '100%',
                }}
              />
            </div>
          </div>
          {!disabled && (
            <Stack
              flex="1"
              direction="row"
              justifyContent="end"
              alignItems="end"
            >
              {/* <Stack direction={'row'} flex="1" gap={1}>
              <Tooltip title="Aggiorna anteprima">
                <IconButton
                  sx={{ height: '40px', width: '40px' }}
                  size="small"
                  onClick={() => {
                    setTimeStamp(Date.now());
                    setTimeout(() => {
                      setRefresh(!refresh);
                    }, 600);
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Salva anteprima">
                <IconButton
                  sx={{ height: '40px', width: '40px' }}
                  size="small"
                  onClick={handleSavePreview}
                >
                  <SaveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Prova anteprima">
                <IconButton
                  sx={{ height: '40px', width: '40px' }}
                  size="small"
                  onClick={handleQrPreview}
                >
                  <QrCodeIcon />
                </IconButton>
              </Tooltip>
            </Stack> */}
              <Button
                // disabled={isDisabled()}
                color="error"
                onClick={() => {
                  // ? RESETTA IL TEMA A QUELLO DI DEFAULT
                  // dispatch(resetCustomTheme());
                  // dispatch(resetPalette());
                  // dispatch(resetFont());
                  // dispatch(resetComponents());

                  // ? CANCELLA LE MODIFICHE
                  const initialShopSign = getDefaultShopSign();
                  if ('configurationJson' in initialShopSign) {
                    const config = JSON.parse(
                      initialShopSign.configurationJson
                    );
                    let customConfig = defaultCustomTheme;
                    if (initialShopSign?.customConfigurationJson) {
                      customConfig = JSON.parse(
                        initialShopSign.customConfigurationJson
                      );
                      dispatch(setCustomTheme(customConfig));
                    }
                    updatePreviewShopSign(shopSignId, config, customConfig);

                    dispatch(setPalette(config));
                    dispatch(setFont(config));
                    dispatch(setComponents(config));

                    //dispatch(changeHolidays(initialHolidays));
                  }
                }}
              >
                Azzera il tema
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
